import { t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { LanguageSelector } from '@repo/ui/language-selector';
import { cn } from '@repo/ui/lib/utils';
import { MenuIcon, XIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import ravenLogo from '@/assets/logo.png';
import { LandingButton } from './landing-button';

const navLinks = [
  {
    get label() {
      return t('workspace.home');
    },
    href: '#home',
  },
  {
    get label() {
      return t('landing.ourSolution');
    },
    href: '#our-solution',
  },
  {
    get label() {
      return t('landing.aboutUs');
    },
    href: '#about-us',
  },
  {
    get label() {
      return t('reports.contact');
    },
    href: '#contact',
  },
];

export const LandingNav = () => {
  useLocale();

  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const container = document.querySelector('.landing-scroll-container') as HTMLElement | null;
    const scrollTarget = container ?? window;

    const onScroll = () => {
      const y = container ? container.scrollTop : window.scrollY;
      setScrolled(y > 12);
    };

    scrollTarget.addEventListener('scroll', onScroll, { passive: true });
    return () => scrollTarget.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={cn(
        'fixed start-0 end-0 top-10 z-50 mx-auto max-w-6xl rounded-md shadow-[0px_2px_10px_3px_#604C671A] transition-all duration-300',
        scrolled
          ? 'border-landing-card-border border-b bg-white shadow-sm backdrop-blur-md'
          : 'bg-landing-bg/80 backdrop-blur-sm',
      )}
    >
      <nav className="flex min-h-16 w-full items-center gap-3 px-4 lg:gap-5 lg:px-8">
        {/* Logo */}
        <a className="flex shrink-0 items-center" href="/">
          <img alt={t('workspace.raven')} className="h-6 w-auto max-w-20 sm:h-8" src={ravenLogo} />
        </a>

        {/* Center nav links */}
        <ul className="m-0 hidden min-w-0 flex-1 list-none items-center justify-center gap-4 p-0 lg:flex xl:gap-8">
          {navLinks.map((link) => (
            <li key={link.href}>
              <a
                className="whitespace-nowrap font-medium text-landing-heading text-sm transition-colors hover:text-primary"
                href={link.href}
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>

        {/* Right CTAs */}
        <div className="ms-auto flex shrink-0 items-center gap-2 lg:ms-0">
          <LanguageSelector />
          <div className="hidden items-center gap-2 lg:flex">
            <LandingButton asChild className="w-auto px-4" size="sm">
              <Link to="/auth/signin">{t('landing.launchPlatform')}</Link>
            </LandingButton>
            <LandingButton asChild className="w-auto px-4" size="sm" variant="ghost">
              <a href="#contact">{t('landing.bookADemo')}</a>
            </LandingButton>
          </div>
          <button
            aria-controls="landing-mobile-menu"
            aria-expanded={isMenuOpen}
            aria-label={isMenuOpen ? t('landing.closeMenu') : t('landing.openMenu')}
            className="inline-flex size-10 items-center justify-center rounded-md text-landing-heading hover:bg-landing-card-bg lg:hidden"
            onClick={() => setIsMenuOpen((wasOpen) => !wasOpen)}
            type="button"
          >
            {isMenuOpen ? <XIcon className="size-5" /> : <MenuIcon className="size-5" />}
          </button>
        </div>
      </nav>
      {isMenuOpen && (
        <div
          className="absolute inset-x-0 top-full mt-1 rounded-md border border-landing-card-border bg-landing-bg p-4 shadow-lg lg:hidden"
          id="landing-mobile-menu"
        >
          <ul className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <li key={link.href}>
                <a
                  className="block rounded-md px-3 py-2 text-landing-heading hover:bg-landing-card-bg"
                  href={link.href}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
          <div className="mt-3 flex flex-wrap gap-2 border-landing-card-border border-t pt-3">
            <LandingButton
              asChild
              className="w-auto px-4"
              onClick={() => setIsMenuOpen(false)}
              size="sm"
            >
              <Link to="/auth/signin">{t('landing.launchPlatform')}</Link>
            </LandingButton>
            <LandingButton
              asChild
              className="w-auto px-4"
              onClick={() => setIsMenuOpen(false)}
              size="sm"
              variant="ghost"
            >
              <a href="#contact">{t('landing.bookADemo')}</a>
            </LandingButton>
          </div>
        </div>
      )}
    </header>
  );
};
